import type { MemoryCategory, ReviewStatus, TagsMode } from '@/api/types'
import { REVIEW_STATUS_ORDER } from '@/lib/orders'
import { integer, oneOf, readSearch, text } from '@/lib/search-params'
import { parseTags, serializeTags } from '@/lib/tags'

export const MEMORY_CATEGORIES = ['knowledge_base', 'long_term_memory'] as const

/**
 * Tri de la table. `file` place les propositions en tête : c'est l'ordre du
 * relecteur. Les deux autres sont ceux que le serveur sait appliquer.
 */
export const MEMORY_SORTS = ['file', '-updated_at', 'name'] as const
export type MemorySort = (typeof MEMORY_SORTS)[number]

export const MEMORY_PAGE_SIZE = 50

/**
 * Filtres de l'espace Mémoire, portés par l'URL : une file filtrée se partage
 * et survit à un rechargement, et le panneau ouvert aussi.
 */
const SHAPE = {
  q: text(),
  client: text(),
  categorie: oneOf(MEMORY_CATEGORIES),
  statut: oneOf(REVIEW_STATUS_ORDER),
  etiquettes: text(),
  /**
   * `ordre` et non `tri` : les schémas de recherche des routes cohabitent
   * dans un même espace typé, et `/tasks` y déclare déjà un `tri` dont les
   * valeurs diffèrent.
   */
  ordre: oneOf(MEMORY_SORTS),
  page: integer({ min: 1 }),
  /** Entrée ouverte dans le panneau latéral. */
  entree: text(),
}

export interface MemorySearch {
  q?: string | undefined
  client?: string | undefined
  categorie?: (typeof MEMORY_CATEGORIES)[number] | undefined
  statut?: (typeof REVIEW_STATUS_ORDER)[number] | undefined
  etiquettes?: string | undefined
  ordre?: MemorySort | undefined
  page?: number | undefined
  entree?: string | undefined
}

export function parseMemorySearch(search: Record<string, unknown>): MemorySearch {
  return readSearch(SHAPE, search)
}

export const DEFAULT_MEMORY_SORT: MemorySort = 'file'

export interface MemoryQuery {
  client_id?: string | undefined
  category?: MemoryCategory | undefined
  review_status: string
  tags?: string | undefined
  tags_mode?: TagsMode | undefined
  q?: string | undefined
  include_expired: boolean
  sort: '-updated_at' | 'name'
  page: number
  page_size: number
}

/**
 * Traduit les filtres d'URL en query pour `GET /memory`.
 *
 * Le serveur ne renvoie que les mémoires **actives** par défaut : sans lui
 * demander explicitement les autres, la file de validation serait toujours
 * vide, ce qui est la pire façon de manquer une proposition.
 */
export function toMemoryQuery(
  search: MemorySearch,
  allStatuses: readonly ReviewStatus[],
): MemoryQuery {
  const tags = parseTags(search.etiquettes)
  const q = search.q?.trim()
  const sort = search.ordre ?? DEFAULT_MEMORY_SORT

  return {
    client_id: search.client,
    category: search.categorie,
    review_status: (search.statut ? [search.statut] : allStatuses).join(','),
    tags: serializeTags(tags),
    tags_mode: tags.length > 1 ? 'all' : undefined,
    q: q === '' ? undefined : q,
    include_expired: true,
    // `file` n'existe pas côté serveur : il se réduit à la récence, que la
    // table réordonne ensuite pour remonter les propositions.
    sort: sort === 'name' ? 'name' : '-updated_at',
    page: search.page ?? 1,
    page_size: MEMORY_PAGE_SIZE,
  }
}

export function hasActiveMemoryFilters(search: MemorySearch): boolean {
  return !!(search.q ?? search.client ?? search.categorie ?? search.statut ?? search.etiquettes)
}

export const EMPTY_MEMORY_FILTERS = {
  q: undefined,
  client: undefined,
  categorie: undefined,
  statut: undefined,
  etiquettes: undefined,
  page: undefined,
} satisfies Partial<MemorySearch>
